import ArrowBackIcon from "@mui/icons-material/ArrowBackRounded";
import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import { Button, FormControlLabel, IconButton, Stack, Switch, Typography } from "@mui/material";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import ActionsMenuButton from "@entrilia/shared/components/ActionsMenuButton";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import type { Inbox } from "../../../../api/types/inboxesTypes";
import useClientContext from "../../../../context/useClientContext";
import { pageRoutes } from "../../../../routes";
import GeneralPageHeader from "../../../common/GeneralPageHeader";
import AgentInboxChannelStatusChip from "./AgentInboxChannelStatusChip";
import AgentInboxSettingsContext from "./AgentInboxSettingsContext";
import DeleteAgentInboxDialog from "./DeleteAgentInboxDialog";
import DeleteInboxChannelDialog from "./DeleteInboxChannelDialog";
import {
  changeChannelActiveStateAction,
  finishEditingChannelAction,
  getUpdateInboxRequest,
  resetEditingChannelAction,
} from "./editInboxChannelState";

interface DialogState {
  openDialog?: "delete_inbox" | "delete_channel";
}

const updateInbox = withErrorHandling(adminApi.updateInbox);

const AgentInboxSettingsPageHeader = () => {
  const { clientCode } = useClientContext();
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const [, setSearchParams] = useSearchParams();

  const {
    hasEditPermissions,
    editedChannelState,
    setEditedChannelState,
    inbox,
    onInboxUpdated,
    editedChannelValidationResults,
  } = useDefinedContext(AgentInboxSettingsContext);

  const navigate = useNavigate();

  const [dialogState, setDialogState] = useState<DialogState>({});
  const [saving, setSaving] = useState(false);

  const backRoute = `/${clientCode}/${pageRoutes.integration}/${pageRoutes.agenticWorkflows}/?tab=inboxes`;

  const handleInboxDeleted = () => {
    navigate(backRoute);
  };

  const handleChannelDeleted = (updatedInbox: Inbox) => {
    setEditedChannelState(finishEditingChannelAction());
    onInboxUpdated(updatedInbox);
    setSearchParams({});
  };

  const handleReset = () => {
    setEditedChannelState(resetEditingChannelAction(inbox.channels));
  };

  const handleSave = async () => {
    if (editedChannelState === undefined) {
      return;
    }

    setSaving(true);

    const payload = getUpdateInboxRequest(editedChannelState, inbox, clientCode);
    const [resp, error] = await updateInbox(inbox.id, payload);

    setSaving(false);

    if (error) {
      logError(error, "[AgentInboxSettingsPageHeader] updateInbox");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Changes saved successfully");
    onInboxUpdated(resp);
    setEditedChannelState(undefined);
  };

  if (editedChannelState === undefined) {
    return (
      <>
        <GeneralPageHeader
          TitleComponent={
            <Stack spacing={0.5}>
              <Typography variant="h6">{inbox.name}</Typography>
              <Typography variant="caption" color="textSecondary">
                Agent Inbox
              </Typography>
            </Stack>
          }
          showDefaultBackButtonTo={backRoute}
        >
          {hasEditPermissions && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDialogState({ openDialog: "delete_inbox" })}
            >
              Delete
            </Button>
          )}
        </GeneralPageHeader>
        <DeleteAgentInboxDialog
          inbox={inbox}
          open={dialogState.openDialog === "delete_inbox"}
          onClose={() => setDialogState({})}
          onDeleted={handleInboxDeleted}
        />
      </>
    );
  }

  const handleBack = () => {
    setEditedChannelState(finishEditingChannelAction());
    setSearchParams({});
  };

  return (
    <>
      <GeneralPageHeader
        BackButton={
          <IconButton onClick={handleBack}>
            <ArrowBackIcon />
          </IconButton>
        }
        TitleComponent={
          <Stack spacing={0.5}>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <Typography variant="h6">{editedChannelState.channelForm.name}</Typography>
              <AgentInboxChannelStatusChip
                isActive={Boolean(inbox.channels.find((c) => c.id === editedChannelState.channelId)?.isActive)}
              />
            </Stack>
            <Typography variant="caption" color="textSecondary">
              Inbox Channel
            </Typography>
          </Stack>
        }
      >
        <FormControlLabel
          control={
            <Switch
              checked={editedChannelState.channelForm.isActive}
              onChange={(_, checked) => setEditedChannelState(changeChannelActiveStateAction(checked))}
            />
          }
          label={<Typography>Activate Channel</Typography>}
          labelPlacement="start"
        />
        <Button variant="text" color="secondary" onClick={handleReset} disabled={!editedChannelState.isDirty}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          loading={saving}
          onClick={handleSave}
          disabled={!editedChannelState.isDirty || !editedChannelValidationResults?.isValid}
        >
          Save
        </Button>

        {!editedChannelState.isNewChannel && (
          <ActionsMenuButton
            items={[
              {
                label: "Delete",
                icon: <DeleteIcon color="error" />,
                onClick: () => setDialogState({ openDialog: "delete_channel" }),
              },
            ]}
          />
        )}
      </GeneralPageHeader>
      {dialogState.openDialog === "delete_channel" && editedChannelState.channelId && (
        <DeleteInboxChannelDialog
          channelId={editedChannelState.channelId}
          onClose={() => setDialogState({})}
          onDeleted={handleChannelDeleted}
        />
      )}
    </>
  );
};

export default AgentInboxSettingsPageHeader;
